import { Controller } from "@hotwired/stimulus"
import { embedDashboard } from "@superset-ui/embedded-sdk";


export default class extends Controller {

  static values = {
    reportId: String,
    guestToken: String,
    refreshUrl: String,
    embedDashboardContainer: String,
    organizationId: String,
    supersetDomain: String
  }

  connect() {
    this.embedReport()
  }

  embedReport() {
    embedDashboard({
      id: this.reportIdValue, // dynamically pass the dashboard ID from all my dashboards
      // Origin is server-provided (ENV SUPERSET_EMBED_DOMAIN) so embeds can move to a cookieless
      // host without a JS change; falls back to the default Superset origin when unset.
      supersetDomain: this.supersetDomainValue || "https://superset.scientist.com",
      mountPoint: document.getElementById(this.embedDashboardContainerValue),
      // The SDK re-invokes fetchGuestToken shortly before each token expires. Returning a
      // static token here (the old behaviour) meant every refresh handed back the same,
      // already-expired token, so charts 401'd after GUEST_TOKEN_JWT_EXP_SECONDS. Re-mint
      // server-side on each refresh instead (mirrors the Reveal BI refresh_token pattern).
      fetchGuestToken: () => this.fetchGuestToken(),
      dashboardUiConfig: {
        hideTitle: true,
        filters: {
          expanded: true,
        },
        urlParams: {},
      },
    })
  }

  async fetchGuestToken() {
    // Use the server-rendered token for the initial mount (already minted and
    // access-checked during the page request); re-mint via the endpoint on every
    // subsequent refresh so the session never expires. The SDK drives the timing
    // (~5s before exp), so no interval of our own is needed.
    if (this.guestTokenValue && !this.hasRefreshed) {
      this.hasRefreshed = true
      return this.guestTokenValue
    }

    try {
      const headers = { Accept: "application/json" }
      const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
      if (csrfToken) headers["X-CSRF-Token"] = csrfToken

      const response = await fetch(this.refreshUrlValue, {
        headers,
        credentials: "same-origin",
      })

      if (response.ok) {
        const data = await response.json()
        this.guestTokenValue = data.token // cache latest so a later failure can fall back
        return data.token
      }
    } catch {
      // Network error — fall through and return the last-known token so the SDK's
      // refresh loop keeps retrying rather than dying on an unhandled rejection.
    }

    // On failure, return the last-known token: the SDK reschedules another refresh
    // shortly after, so a transient failure self-heals on the next attempt.
    return this.guestTokenValue
  }
}
